import type { EventTemplate, NostrEvent } from "@setu/protocol";
import {
  BlossomError,
  blossomAuthHeader,
  blossomUploadUrl,
  buildBlossomAuth,
  parseBlobDescriptor,
} from "@setu/protocol";
import { MAX_UPLOAD_BYTES, UploadError, type UploadedMedia } from "./nip96";

/**
 * Putting a file on a Blossom server (BUD-01/02) and getting back something we are
 * willing to publish.
 *
 * The order of operations is the security story, and it is the opposite of the NIP-96
 * path: **hash first, then ask permission, then compare.** The file is read and
 * hashed locally, the kind-24242 authorizes *that hash*, and the descriptor the
 * server returns has to name the same one. So a server cannot silently store
 * something else — a re-encode, a truncation, a substituted file — and have the user
 * sign a note pointing at it. `packages/protocol/src/blossom.ts` holds every check;
 * this module is the I/O around them.
 *
 * Two decisions worth stating, because both are trade-offs rather than obvious:
 *
 *  - **One signature per file, not per server.** A kind-24242 carries no `u` tag, so
 *    it is valid at every Blossom server — which means the one we hand it to could
 *    replay it to another and store the same bytes there under the user's pubkey.
 *    That is the whole extent of what it authorizes, it lasts under a minute, and the
 *    alternative is a signer prompt per server on a failover list. Prompting three
 *    times for one attachment is how a user learns to click through prompts.
 *  - **First server that accepts, wins.** BUD-03 lists servers so a client *can*
 *    mirror to all of them; doing that from a composer multiplies the upload over a
 *    phone connection for redundancy nobody asked for at the moment they are trying
 *    to post. Mirroring (BUD-04) is a background job, not part of pressing attach,
 *    and is deliberately not implemented here.
 */

/** What the uploader needs from the outside world, injected for testability. */
export interface BlossomUploadDeps {
  readonly fetch: typeof globalThis.fetch;
  readonly sign: (template: EventTemplate) => Promise<NostrEvent>;
}

export interface BlossomUploadInput {
  /** Validated server origins, in the user's stated order. */
  readonly servers: readonly string[];
  readonly file: File;
}

/** Lowercase hex sha256 of some bytes. The blob's name, in Blossom terms. */
export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  let out = "";
  for (const byte of new Uint8Array(digest)) {
    out += byte.toString(16).padStart(2, "0");
  }
  return out;
}

/** Hostname of a server origin we have already validated. */
function label(server: string): string {
  try {
    return new URL(server).hostname;
  } catch {
    return server;
  }
}

/**
 * Why one server refused, in a form a reader can act on.
 *
 * BUD-01 asks servers to explain themselves in `X-Reason` precisely because a bare
 * 403 tells the person holding the file nothing. Truncated: this string is
 * server-controlled and ends up in the composer's error line.
 */
async function rejectionReason(
  response: Response,
  host: string,
): Promise<string> {
  const stated = response.headers.get("X-Reason")?.trim();
  if (stated) return `${host}: ${stated.slice(0, 120)}`;
  return `${host}: HTTP ${response.status}`;
}

async function putBlob(
  server: string,
  file: File,
  hash: string,
  auth: NostrEvent,
  deps: BlossomUploadDeps,
): Promise<UploadedMedia> {
  const response = await deps.fetch(blossomUploadUrl(server), {
    method: "PUT",
    headers: {
      Authorization: blossomAuthHeader(auth),
      // Sent when the browser reported one: BUD-02 lets the server record the type
      // it was given, and a blob stored as `application/octet-stream` is one a
      // reader's client will refuse to render inline.
      ...(file.type ? { "Content-Type": file.type } : {}),
    },
    // The raw file as the body. Blossom has no multipart form and no field names —
    // the bytes on the wire are exactly the bytes that were hashed, which is what
    // makes the hash comparison below meaningful.
    body: file,
  });

  if (!response.ok) {
    throw new UploadError(
      "upload-failed",
      await rejectionReason(response, label(server)),
    );
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new UploadError(
      "bad-response",
      `${label(server)} accepted the file but returned no descriptor.`,
    );
  }

  const descriptor = parseBlobDescriptor(body, {
    sha256: hash,
    size: file.size,
  });

  // Deliberately the same shape the NIP-96 path produces, so the composer's `imeta`
  // tags are identical whichever protocol carried the file. `dim` is absent because
  // Blossom servers do not measure images and this module does not either — a
  // guessed dimension is worse than none.
  return {
    url: descriptor.url,
    ...(descriptor.type || file.type
      ? { mimeType: descriptor.type ?? file.type }
      : {}),
    hash: descriptor.sha256,
  };
}

/**
 * Upload one file to the first server that will take it.
 *
 * Throws `UploadError` for anything a reader needs to see, including the case where
 * every server refused — the individual reasons are collected, because "upload
 * failed" with three servers configured leaves nobody able to work out which one is
 * broken.
 */
export async function uploadToBlossom(
  { servers, file }: BlossomUploadInput,
  deps: BlossomUploadDeps,
): Promise<UploadedMedia> {
  if (servers.length === 0) {
    throw new UploadError(
      "no-servers",
      "No Blossom server is configured, so there is nowhere to put this file.",
    );
  }
  // The same ceiling as the NIP-96 path, checked before the file is read rather than
  // after the whole body has been sent and rejected.
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new UploadError(
      "too-large",
      `That file is ${Math.round(file.size / 1024 / 1024)}MB. The limit is ${
        MAX_UPLOAD_BYTES / 1024 / 1024
      }MB.`,
    );
  }

  const hash = await sha256Hex(await file.arrayBuffer());
  const auth = await deps.sign(
    buildBlossomAuth({
      sha256: hash,
      // Named so the signer prompt says what is being authorized. A signer that
      // shows "kind 24242" and nothing else trains people to approve blindly.
      reason: `Upload ${file.name || "a file"}`,
    }),
  );

  const failures: string[] = [];
  for (const server of servers) {
    try {
      return await putBlob(server, file, hash, auth, deps);
    } catch (error) {
      // A hash or descriptor mismatch is not a reason to try the next server: it
      // means this server answered dishonestly, and continuing would hide that
      // behind a success from somewhere else. Everything else — unreachable, full,
      // 5xx — is worth a failover.
      if (
        error instanceof BlossomError &&
        (error.code === "hash-mismatch" || error.code === "size-mismatch")
      ) {
        throw error;
      }
      failures.push(
        error instanceof UploadError || error instanceof BlossomError
          ? error.message
          : `${label(server)} could not be reached.`,
      );
    }
  }

  throw new UploadError(
    "upload-failed",
    servers.length === 1
      ? (failures[0] ?? "The upload failed.")
      : `No Blossom server accepted the file. ${failures.join("; ")}`,
  );
}
